/** Monday-first display order of the backend's weekdays (0 = Sunday … 6 = Saturday). */
export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

export type HourlySlotInsight = { weekday: number; hour: number; posts: number; median_views?: number | null };
export type HeatmapCell = {
  weekday: number;
  hour: number;
  posts: number;
  medianViews: number | null;
  /** 0–1 relative to the best cell; 0 for cells without posts. */
  intensity: number;
};
export type HeatmapGrid = {
  weekdays: readonly number[];
  rows: Array<{ hour: number; cells: HeatmapCell[] }>;
  best: HeatmapCell | null;
};

/**
 * Weekday × local-hour grid: 24 rows, Monday-first columns. Every cell with a post is coloured
 * (no minimum — the UI shows each cell's post count); the best cell has the highest median,
 * ties going to the cell with more posts.
 */
export function buildHeatmapGrid(slots: readonly HourlySlotInsight[]): HeatmapGrid {
  const byKey = new Map(slots.map((slot) => [`${slot.weekday}:${slot.hour}`, slot]));
  const base = HOURS.map((hour) => ({
    hour,
    cells: WEEKDAY_ORDER.map((weekday) => {
      const slot = byKey.get(`${weekday}:${hour}`);
      const medianViews = typeof slot?.median_views === 'number' && Number.isFinite(slot.median_views)
        ? slot.median_views
        : null;
      return { weekday, hour, posts: medianViews === null ? 0 : slot?.posts ?? 0, medianViews, intensity: 0 };
    }),
  }));
  const withData = base.flatMap((row) => row.cells).filter((cell) => cell.medianViews !== null);
  const max = Math.max(0, ...withData.map((cell) => cell.medianViews ?? 0));
  const rows = base.map((row) => ({
    hour: row.hour,
    cells: row.cells.map((cell) => ({
      ...cell,
      intensity: cell.medianViews !== null && max > 0 ? cell.medianViews / max : 0,
    })),
  }));
  const best = rows
    .flatMap((row) => row.cells)
    .filter((cell) => cell.medianViews !== null)
    .reduce<HeatmapCell | null>((top, cell) => {
      if (!top) return cell;
      const [cellViews, topViews] = [cell.medianViews ?? 0, top.medianViews ?? 0];
      return cellViews > topViews || (cellViews === topViews && cell.posts > top.posts) ? cell : top;
    }, null);
  return { weekdays: WEEKDAY_ORDER, rows, best };
}
